import React from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiFileText,
  FiGlobe,
  FiLock,
  FiMail,
  FiShield,
  FiUserCheck,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./TermsConditions.css";

const TermsConditions = () => {
  return (
    <>
      <Helmet>
        <title>
          Terms & Conditions | Dropgear IO Private Limited
        </title>

        <meta
          name="description"
          content="Terms and Conditions governing access to and use of Dropgear IO Private Limited websites, software products, AI services, and related services."
        />
      </Helmet>

      <Header />

      <main className="dg-terms-page">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="dg-terms-hero">

          <div className="dg-terms-container">

            <span className="dg-terms-eyebrow">
              LEGAL & TERMS
            </span>

            <h1>
              Terms &
              <span> Conditions</span>
            </h1>

            <p>
              These Terms & Conditions explain the rules and conditions
              governing your access to and use of Dropgear IO websites,
              software, AI-powered products, platforms, and related
              services.
            </p>

            <div className="dg-terms-updated">
              <strong>Last Updated:</strong> August 10, 2026
            </div>

          </div>

        </section>


        {/* =====================================================
            CONTENT
        ===================================================== */}

        <section className="dg-terms-content">

          <div className="dg-terms-container">

            <div className="dg-terms-layout">

              {/* =================================================
                  SIDEBAR
              ================================================= */}

              <aside className="dg-terms-sidebar">

                <div className="dg-terms-sidebar-inner">

                  <span>
                    ON THIS PAGE
                  </span>

                  <a href="#acceptance">
                    Acceptance of Terms
                  </a>

                  <a href="#services">
                    Our Services
                  </a>

                  <a href="#accounts">
                    Accounts
                  </a>

                  <a href="#acceptable-use">
                    Acceptable Use
                  </a>

                  <a href="#ai">
                    AI Services
                  </a>

                  <a href="#customer-content">
                    Customer Content
                  </a>

                  <a href="#third-party">
                    Third-Party Services
                  </a>

                  <a href="#intellectual-property">
                    Intellectual Property
                  </a>

                  <a href="#payments">
                    Fees & Payments
                  </a>

                  <a href="#availability">
                    Service Availability
                  </a>

                  <a href="#security">
                    Security
                  </a>

                  <a href="#termination">
                    Termination
                  </a>

                  <a href="#disclaimer">
                    Disclaimers
                  </a>

                  <a href="#liability">
                    Limitation of Liability
                  </a>

                  <a href="#indemnification">
                    Indemnification
                  </a>

                  <a href="#changes">
                    Changes to Terms
                  </a>

                  <a href="#contact">
                    Contact Us
                  </a>

                </div>

              </aside>


              {/* =================================================
                  ARTICLE
              ================================================= */}

              <article className="dg-terms-article">

                {/* =================================================
                    1
                ================================================= */}

                <section id="acceptance">

                  <div className="dg-terms-section-icon">
                    <FiCheckCircle />
                  </div>

                  <h2>
                    1. Acceptance of Terms
                  </h2>

                  <p>
                    These Terms & Conditions ("Terms") form an agreement
                    between you and <strong>Dropgear IO Private Limited</strong>
                    ("Dropgear IO", "we", "us", or "our") regarding your
                    access to and use of our websites, software, platforms,
                    AI systems, products, and related services.
                  </p>

                  <p>
                    By accessing, registering for, or using our services,
                    you agree to be bound by these Terms and any additional
                    terms that may apply to specific products or services.
                  </p>

                  <p>
                    If you do not agree with these Terms, you should not
                    access or use the applicable services.
                  </p>

                </section>


                {/* =================================================
                    2
                ================================================= */}

                <section id="services">

                  <div className="dg-terms-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    2. Our Services
                  </h2>

                  <p>
                    Dropgear IO develops and provides software products,
                    SaaS platforms, AI-powered systems, automation tools,
                    intelligent workflows, and related technology services.
                  </p>

                  <p>
                    Specific functionality, availability, pricing, usage
                    limits, integrations, and other characteristics may vary
                    between products and plans.
                  </p>

                  <p>
                    We may modify, improve, replace, suspend, or discontinue
                    particular features or services from time to time.
                  </p>

                </section>


                {/* =================================================
                    3
                ================================================= */}

                <section id="accounts">

                  <div className="dg-terms-section-icon">
                    <FiUserCheck />
                  </div>

                  <h2>
                    3. User Accounts
                  </h2>

                  <p>
                    Certain Dropgear IO services may require you to create
                    an account.
                  </p>

                  <p>
                    You are responsible for providing accurate information
                    and maintaining the confidentiality of your account
                    credentials.
                  </p>

                  <p>
                    You are responsible for activities carried out through
                    your account unless the applicable activity resulted from
                    circumstances outside your reasonable control.
                  </p>

                  <p>
                    If you believe that your account credentials have been
                    compromised or used without authorization, you should
                    notify Dropgear IO as soon as reasonably possible.
                  </p>

                </section>


                {/* =================================================
                    4
                ================================================= */}

                <section id="acceptable-use">

                  <div className="dg-terms-section-icon">
                    <FiShield />
                  </div>

                  <h2>
                    4. Acceptable Use
                  </h2>

                  <p>
                    You agree to use Dropgear IO services only for lawful
                    purposes and in accordance with these Terms.
                  </p>

                  <p>
                    You must not use our services to:
                  </p>

                  <ul>
                    <li>
                      Violate applicable laws or regulations.
                    </li>

                    <li>
                      Infringe the rights of another person or organization.
                    </li>

                    <li>
                      Attempt to gain unauthorized access to our systems,
                      accounts, infrastructure, or networks.
                    </li>

                    <li>
                      Introduce malicious code, harmful software, or
                      disruptive activity.
                    </li>

                    <li>
                      Interfere with the normal operation of our services.
                    </li>

                    <li>
                      Abuse, overload, scrape, or circumvent reasonable
                      technical restrictions of our systems.
                    </li>

                    <li>
                      Use our services for fraudulent, deceptive, or harmful
                      activities.
                    </li>
                  </ul>

                </section>


                {/* =================================================
                    5
                ================================================= */}

                <section id="ai">

                  <div className="dg-terms-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    5. AI-Powered Services
                  </h2>

                  <p>
                    Some Dropgear IO products use artificial intelligence,
                    machine learning, automated processing, language models,
                    AI agents, or other computational technologies.
                  </p>

                  <p>
                    AI-generated outputs may contain errors, omissions,
                    inaccuracies, or unexpected results. AI-generated
                    information should therefore be reviewed and validated
                    before being used for important business, financial,
                    legal, medical, operational, or other consequential
                    decisions.
                  </p>

                  <p>
                    Unless specifically stated otherwise in an applicable
                    agreement or product documentation, Dropgear IO does not
                    represent that AI-generated outputs will always be
                    accurate, complete, reliable, or suitable for a
                    particular purpose.
                  </p>

                  <div className="dg-terms-highlight">

                    <strong>
                      Important:
                    </strong>

                    <span>
                      Users remain responsible for reviewing outputs and
                      determining whether they are appropriate for their
                      intended use.
                    </span>

                  </div>

                </section>


                {/* =================================================
                    6
                ================================================= */}

                <section id="customer-content">

                  <div className="dg-terms-section-icon">
                    <FiFileText />
                  </div>

                  <h2>
                    6. Customer Content & Data
                  </h2>

                  <p>
                    Depending on the service, you may submit text, files,
                    instructions, business information, data, or other
                    content to Dropgear IO.
                  </p>

                  <p>
                    You are responsible for ensuring that you have the
                    necessary rights, permissions, and lawful basis to submit
                    such content and allow us to process it for the requested
                    service.
                  </p>

                  <p>
                    You should not submit confidential, highly sensitive, or
                    regulated information unless the applicable Dropgear IO
                    service and agreement specifically support such
                    processing.
                  </p>

                  <p>
                    Our handling of personal information is additionally
                    described in our Privacy Policy.
                  </p>

                </section>


                {/* =================================================
                    7
                ================================================= */}

                <section id="third-party">

                  <div className="dg-terms-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    7. Third-Party Services & Integrations
                  </h2>

                  <p>
                    Dropgear IO services may integrate with or provide links
                    to third-party platforms, applications, APIs, hosting
                    providers, communication services, or other technologies.
                  </p>

                  <p>
                    Third-party services may have their own terms,
                    conditions, privacy policies, availability limitations,
                    and security practices.
                  </p>

                  <p>
                    Your use of third-party services is subject to the terms
                    applicable to those services. Dropgear IO is not
                    responsible for the independent operation or policies of
                    third-party providers.
                  </p>

                </section>


                {/* =================================================
                    8
                ================================================= */}

                <section id="intellectual-property">

                  <div className="dg-terms-section-icon">
                    <FiLock />
                  </div>

                  <h2>
                    8. Intellectual Property
                  </h2>

                  <p>
                    Unless otherwise stated, Dropgear IO and its licensors
                    retain all rights, title, and interest in our websites,
                    software, platforms, product designs, interfaces,
                    trademarks, logos, documentation, technology, and other
                    proprietary materials.
                  </p>

                  <p>
                    These Terms do not transfer ownership of Dropgear IO
                    intellectual property to you.
                  </p>

                  <p>
                    You may use our services only as permitted by these Terms,
                    applicable product documentation, and any separate
                    agreement between you and Dropgear IO.
                  </p>

                </section>


                {/* =================================================
                    9
                ================================================= */}

                <section id="payments">

                  <div className="dg-terms-section-icon">
                    <FiFileText />
                  </div>

                  <h2>
                    9. Fees & Payments
                  </h2>

                  <p>
                    Certain Dropgear IO services may require payment of fees.
                    Applicable pricing, billing periods, usage limits, taxes,
                    and payment conditions will be communicated through the
                    relevant product or commercial agreement.
                  </p>

                  <p>
                    Where applicable, subscription charges may continue for
                    the selected subscription period unless cancelled in
                    accordance with the applicable cancellation terms.
                  </p>

                  <p>
                    We may update pricing or introduce new pricing structures
                    in accordance with applicable agreements and applicable
                    law.
                  </p>

                </section>


                {/* =================================================
                    10
                ================================================= */}

                <section id="availability">

                  <div className="dg-terms-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    10. Service Availability
                  </h2>

                  <p>
                    We aim to provide reliable and useful services, but we do
                    not guarantee that every service or feature will always
                    be available, uninterrupted, error-free, or compatible
                    with every device, browser, system, or third-party
                    service.
                  </p>

                  <p>
                    Services may occasionally be unavailable because of
                    maintenance, updates, infrastructure issues, technical
                    failures, third-party dependencies, security events, or
                    circumstances beyond our reasonable control.
                  </p>

                </section>


                {/* =================================================
                    11
                ================================================= */}

                <section id="security">

                  <div className="dg-terms-section-icon">
                    <FiShield />
                  </div>

                  <h2>
                    11. Security
                  </h2>

                  <p>
                    We take reasonable measures to protect our systems and
                    services and to reduce the risk of unauthorized access,
                    misuse, or disruption.
                  </p>

                  <p>
                    However, no technology system, network, or internet
                    transmission can be guaranteed to be completely secure.
                  </p>

                  <p>
                    You are responsible for maintaining appropriate security
                    practices for your own accounts, devices, credentials,
                    integrations, and submitted content.
                  </p>

                </section>


                {/* =================================================
                    12
                ================================================= */}

                <section id="termination">

                  <div className="dg-terms-section-icon">
                    <FiUserCheck />
                  </div>

                  <h2>
                    12. Suspension & Termination
                  </h2>

                  <p>
                    You may stop using our services at any time, subject to
                    any applicable contractual or subscription obligations.
                  </p>

                  <p>
                    We may suspend or terminate access where reasonably
                    necessary to protect our services, users, systems, or
                    legitimate business interests, including where a user
                    materially violates these Terms or applicable law.
                  </p>

                  <p>
                    Certain provisions of these Terms that by their nature
                    should continue after termination may remain applicable,
                    including provisions concerning intellectual property,
                    disclaimers, limitations of liability, and dispute
                    resolution.
                  </p>

                </section>


                {/* =================================================
                    13
                ================================================= */}

                <section id="disclaimer">

                  <div className="dg-terms-section-icon">
                    <FiShield />
                  </div>

                  <h2>
                    13. Disclaimers
                  </h2>

                  <p>
                    To the maximum extent permitted by applicable law, our
                    services are provided on an "as available" and
                    "as is" basis.
                  </p>

                  <p>
                    We do not guarantee that our services will satisfy every
                    specific business requirement, produce a particular
                    outcome, or operate without interruption or errors.
                  </p>

                  <p>
                    Information provided through our website or services is
                    not intended to constitute legal, financial, medical,
                    investment, or other professional advice unless expressly
                    stated otherwise.
                  </p>

                </section>


                {/* =================================================
                    14
                ================================================= */}

                <section id="liability">

                  <div className="dg-terms-section-icon">
                    <FiLock />
                  </div>

                  <h2>
                    14. Limitation of Liability
                  </h2>

                  <p>
                    To the maximum extent permitted by applicable law,
                    Dropgear IO and its directors, officers, employees,
                    affiliates, partners, and service providers will not be
                    responsible for indirect, incidental, special,
                    consequential, or exemplary losses arising from or
                    relating to your use of the services.
                  </p>

                  <p>
                    This section does not exclude or limit liability where
                    such exclusion or limitation is prohibited by applicable
                    law.
                  </p>

                  <p>
                    Where a separate written agreement between you and
                    Dropgear IO contains specific liability terms, those
                    terms may govern the applicable service.
                  </p>

                </section>


                {/* =================================================
                    15
                ================================================= */}

                <section id="indemnification">

                  <div className="dg-terms-section-icon">
                    <FiUserCheck />
                  </div>

                  <h2>
                    15. Indemnification
                  </h2>

                  <p>
                    To the extent permitted by applicable law, you agree to
                    take reasonable responsibility for claims, losses,
                    liabilities, or expenses arising from your unlawful use
                    of the services, violation of these Terms, or infringement
                    of another party's rights.
                  </p>

                  <p>
                    This provision applies subject to applicable law and any
                    separate agreement governing your relationship with
                    Dropgear IO.
                  </p>

                </section>


                {/* =================================================
                    16
                ================================================= */}

                <section>

                  <div className="dg-terms-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    16. Governing Law
                  </h2>

                  <p>
                    These Terms are intended to be interpreted in accordance
                    with applicable laws of India, subject to any mandatory
                    legal rights or requirements that may apply to you.
                  </p>

                  <p>
                    Any disputes arising in connection with these Terms will
                    be handled in accordance with applicable law and the
                    appropriate jurisdiction.
                  </p>

                  <p>
                    Where a separate written agreement specifies governing
                    law or jurisdiction, that agreement may take precedence
                    for the services covered by that agreement.
                  </p>

                </section>


                {/* =================================================
                    17
                ================================================= */}

                <section id="changes">

                  <div className="dg-terms-section-icon">
                    <FiFileText />
                  </div>

                  <h2>
                    17. Changes to These Terms
                  </h2>

                  <p>
                    We may update these Terms from time to time to reflect
                    changes to our products, services, technology, business
                    practices, or applicable legal requirements.
                  </p>

                  <p>
                    When we make changes, we will publish the updated Terms
                    on this page and update the "Last Updated" date.
                  </p>

                  <p>
                    Your continued use of the applicable services after
                    updated Terms become effective may constitute acceptance
                    of the revised Terms, to the extent permitted by law.
                  </p>

                </section>


                {/* =================================================
                    18
                ================================================= */}

                <section>

                  <div className="dg-terms-section-icon">
                    <FiCheckCircle />
                  </div>

                  <h2>
                    18. Severability
                  </h2>

                  <p>
                    If any provision of these Terms is determined to be
                    invalid, unlawful, or unenforceable, that provision will
                    be interpreted or limited to the extent necessary, and
                    the remaining provisions will continue to apply to the
                    extent permitted by law.
                  </p>

                </section>


                {/* =================================================
                    19
                ================================================= */}

                <section>

                  <div className="dg-terms-section-icon">
                    <FiFileText />
                  </div>

                  <h2>
                    19. Entire Agreement
                  </h2>

                  <p>
                    These Terms, together with any applicable product
                    documentation, policies, order forms, subscription terms,
                    or separate written agreements, constitute the applicable
                    understanding between you and Dropgear IO regarding the
                    relevant services.
                  </p>

                  <p>
                    Where a separate written agreement conflicts with these
                    Terms, the applicable written agreement may govern the
                    services covered by that agreement.
                  </p>

                </section>


                {/* =================================================
                    CONTACT
                ================================================= */}

                <section id="contact">

                  <div className="dg-terms-section-icon">
                    <FiMail />
                  </div>

                  <h2>
                    20. Contact Us
                  </h2>

                  <p>
                    If you have questions about these Terms & Conditions,
                    please contact Dropgear IO Private Limited.
                  </p>

                  <div className="dg-terms-contact-card">

                    <div>

                      <span>
                        COMPANY
                      </span>

                      <strong>
                        Dropgear IO Private Limited
                      </strong>

                    </div>


                    <div>

                      <span>
                        EMAIL
                      </span>

                      <a href="mailto:admin@aidropgerio.in">
                        admin@aidropgerio.in
                      </a>

                    </div>

                  </div>

                </section>


                {/* =================================================
                    FINAL MESSAGE
                ================================================= */}

                <div className="dg-terms-final">

                  <FiShield />

                  <p>
                    Dropgear IO is building AI-powered software with a
                    practical focus on useful automation, responsible
                    technology, and transparent product development.
                  </p>

                </div>

              </article>

            </div>

          </div>

        </section>


        {/* =====================================================
            BACK TO TOP
        ===================================================== */}

        <div className="dg-terms-back">

          <div className="dg-terms-container">

            <a href="#">
              <FiArrowLeft />
              Back to top
            </a>

          </div>

        </div>

      </main>

      <Footer />
    </>
  );
};

export default TermsConditions;

